import React, { useState, useEffect, useRef } from 'react';
import { fetchData } from '../Firebase/realtimedatabase';
import { trackVisit } from '../Firebase/tracking';
import { fetchImageFromStorage } from '../Firebase/storage';
import './Home.css';
import './nav-components.css';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import ReviewsCarousel from './ReviewsCarousel';
import {
  Gauge, Thermometer, ShieldCheck, Wifi, Bluetooth, RefreshCw, Cable, Zap,
  Ruler, Volume2, Tag, Building2, Palette, Database,
  Cpu as CpuIcon, HardDrive, Settings2,
} from 'lucide-react';

// Import all icons
import batteryIcon from '../Assets/battery.png';
import displayIcon from '../Assets/display.png';
import cpuClockSpeedIcon from '../Assets/cpu_clock_speed.png';
import refreshRateIcon from '../Assets/refresh_rate.png';
import processorIcon from '../Assets/processor.png';
import ramIcon from '../Assets/ram.png';
import storageIcon from '../Assets/storage.png';
import osIcon from '../Assets/os.png';
import gpuIcon from '../Assets/gpu.png';
import backIcon from '../Assets/back.png';
import shareIcon from '../Assets/share.png';
import closeIcon from '../Assets/close.png';
import ownerpic from '../Assets/Square_logo.jpeg';
import phoneIcon from '../Assets/phone.png';
import whatsappIcon from '../Assets/whatsapp.png';
import instagramIcon from '../Assets/instagram.png';
import facebookIcon from '../Assets/facebook.png';
import gmailIcon from '../Assets/gmail.png';
import keyboardIcon from '../Assets/keyboard.png';
import mouseIcon from '../Assets/mouse.png';
import motherboardIcon from '../Assets/motherboard.png';
import networkIcon from '../Assets/network.png';
import cabinetIcon from '../Assets/cabinet.png';
import joystickIcon from '../Assets/joystick.png';
import cpuFanIcon from '../Assets/cpu_fan.png';
import laptopIcon from '../Assets/laptop.png';
import intelIcon from '../Assets/intel.png';
import amdIcon from '../Assets/amd.png';
import nvidiaIcon from '../Assets/nvidia.png';

function fmtPrice(p) {
  const n = Number(p);
  return isNaN(n) ? p : n.toLocaleString('en-IN');
}

// Picks the brand badges shown in the top-right of a product card:
//   • exactly one CPU badge — AMD (Ryzen/Athlon/AMD) or Intel (Core iX / Core
//     Ultra / Ultra / Pentium / Celeron / Intel). \b word-boundaries keep
//     "ultra" from matching "ultrabook" and "i5" from matching mid-word.
//   • plus an NVIDIA badge when a discrete GeForce/RTX/GTX/NVIDIA GPU is named.
function getBrandBadges(item) {
  const cpuText = `${item.Processor || ''} ${item.Name || ''}`.toLowerCase();
  const gpuText = `${item.GPU || ''} ${item.Name || ''}`.toLowerCase();
  const badges = [];

  const isAmd = /\b(amd|ryzen|athlon)\b/.test(cpuText);
  const isIntel = /\b(core\s*)?i[3579]\b/.test(cpuText)
    || /\bcore\s*ultra\b/.test(cpuText)
    || /\bultra\b/.test(cpuText)
    || /\b(intel|pentium|celeron)\b/.test(cpuText);
  if (isAmd) badges.push({ src: amdIcon, alt: 'AMD' });
  else if (isIntel) badges.push({ src: intelIcon, alt: 'Intel' });

  if (/\b(nvidia|geforce|rtx|gtx)\b/.test(gpuText)) {
    badges.push({ src: nvidiaIcon, alt: 'NVIDIA Graphics' });
  }
  return badges;
}

// Known field names get a dedicated PNG icon (matches the rest of the site's icon style).
const ICON_MAP = {
  display: displayIcon,
  screen: displayIcon,
  resolution: displayIcon,
  refreshrate: refreshRateIcon,
  ram: ramIcon,
  memory: ramIcon,
  storage: storageIcon,
  capacity: storageIcon,
  processor: processorIcon,
  cpu: processorIcon,
  chipset: processorIcon,
  socket: processorIcon,
  clockspeed: cpuClockSpeedIcon,
  os: osIcon,
  gpu: gpuIcon,
  graphics: gpuIcon,
  battery: batteryIcon,
  keyboard: keyboardIcon,
  mouse: mouseIcon,
  motherboard: motherboardIcon,
  network: networkIcon,
  cabinet: cabinetIcon,
  joystick: joystickIcon,
  fan: cpuFanIcon,
  cooling: cpuFanIcon,
  laptop: laptopIcon,
};

// Anything not covered above falls back to an open-source lucide-react icon,
// matched by keyword against the field name, so new/unexpected spec fields
// from Firebase always get *some* relevant icon instead of none at all.
const LUCIDE_ICON_MAP = {
  clock: Gauge,
  speed: Gauge,
  frequency: Gauge,
  tdp: Thermometer,
  thermal: Thermometer,
  temperature: Thermometer,
  warranty: ShieldCheck,
  wifi: Wifi,
  wireless: Wifi,
  connectivity: Wifi,
  bluetooth: Bluetooth,
  refresh: RefreshCw,
  interface: Cable,
  port: Cable,
  cable: Cable,
  power: Zap,
  wattage: Zap,
  voltage: Zap,
  psu: Zap,
  amp: Zap,
  dimension: Ruler,
  size: Ruler,
  length: Ruler,
  width: Ruler,
  height: Ruler,
  weight: Ruler,
  noise: Volume2,
  sound: Volume2,
  audio: Volume2,
  brand: Tag,
  model: Tag,
  type: Tag,
  manufacturer: Building2,
  color: Palette,
  colour: Palette,
  cache: Database,
  cores: CpuIcon,
  threads: CpuIcon,
  socket: CpuIcon,
  hdd: HardDrive,
  ssd: HardDrive,
};

const DEFAULT_SPEC_ICON = Settings2;

function getLucideIconForKey(key) {
  const k = String(key).toLowerCase().replace(/[\s_-]+/g, '');
  for (const [keyword, Icon] of Object.entries(LUCIDE_ICON_MAP)) {
    if (k.includes(keyword)) return Icon;
  }
  return DEFAULT_SPEC_ICON;
}

// Renders the PNG icon for a known field, or an appropriate lucide-react icon otherwise.
function SpecIcon({ fieldKey }) {
  const pngIcon = ICON_MAP[String(fieldKey).toLowerCase()];
  if (pngIcon) return <img src={pngIcon} alt={fieldKey} className="detail-icon" />;
  const LucideIcon = getLucideIconForKey(fieldKey);
  return (
    <span className="detail-icon-lucide">
      <LucideIcon size={18} />
    </span>
  );
}

function Home({ fromSplash = false }) {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState('home');
  const [games, setGames] = useState([]);
  const [laptops, setLaptops] = useState([]);
  const [components, setComponents] = useState([]);
  const [desktops, setDesktops] = useState([]);
  const [selectedLaptop, setSelectedLaptop] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [carouselImages, setCarouselImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChatOverlay, setShowChatOverlay] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [imgIsTall, setImgIsTall] = useState(false);
  // Mobile onboarding hint for the hamburger menu — shown ONLY when Home was
  // reached through the fan-splash screen (fromSplash). A plain refresh skips
  // the splash, so the hint doesn't appear. `showMenuHint` is per-session
  // eligibility (cleared once dismissed); `hintVisible` + `hintRect` control
  // the actual timed appearance, measured against the real button so the arrow
  // always points exactly at it (no guessed pixel offsets).
  const [showMenuHint, setShowMenuHint] = useState(fromSplash);
  const [hintVisible, setHintVisible] = useState(false);
  const [hintRect, setHintRect] = useState(null);
  const navToggleRef = useRef(null);

  const fetchedRef = useRef({ laptops: false, components: false, desktops: false, games: false });
  const animationContainerRef = useRef(null);
  const iconsRef = useRef([]);
  const animationFrameId = useRef(null);

  const ICON_MAP = {
    display: displayIcon,
    refreshrate: refreshRateIcon,
    ram: ramIcon,
    storage: storageIcon,
    processor: processorIcon,
    cpu: processorIcon,
    clockspeed: cpuClockSpeedIcon,
    os: osIcon,
    gpu: gpuIcon
  };

  const animatedIcons = [
    processorIcon, gpuIcon, ramIcon, storageIcon,
    displayIcon, osIcon, batteryIcon,
    keyboardIcon, mouseIcon, motherboardIcon, networkIcon,
    cabinetIcon, joystickIcon, cpuFanIcon, laptopIcon
  ];

  useEffect(() => {
    const container = animationContainerRef.current;
    if (!container || (currentSection !== 'home' && currentSection !== 'about')) {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      return;
    }

    const iconElements = Array.from(container.children);
    const iconSize = 50;
    const collisionThreshold = iconSize * 1.5;

    // Icons spawn just off-screen past the left or right edge and drift inward,
    // instead of popping into existence in the middle of the visible area.
    // Vertical position is still spread across rows so they don't all enter at
    // the same height.
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;
    const rows = Math.max(1, Math.ceil(Math.sqrt(iconElements.length)));
    const cellH = containerHeight / rows;

    iconsRef.current = iconElements.map((el, i) => {
        const row = i % rows;
        const cellY = row * cellH;
        const fromLeft = Math.random() < 0.5;
        const speed = 0.3 + Math.random() * 0.5;

        const rotates = Math.random() < 0.4; // only ~40% of icons spin while drifting

        return {
            el,
            x: fromLeft
                ? -iconSize - Math.random() * 150
                : containerWidth + Math.random() * 150,
            y: Math.min(containerHeight - iconSize, cellY + Math.random() * Math.max(0, cellH - iconSize)),
            vx: fromLeft ? speed : -speed,
            vy: (Math.random() - 0.5) * 1,
            isHidden: false,
            hideUntil: 0,
            hasEnteredX: false,
            rotates,
            rotation: rotates ? Math.random() * 360 : 0,
            rotationSpeed: rotates ? (Math.random() - 0.5) * 0.6 : 0,
        };
    });

    const animate = () => {
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      const now = Date.now();

      iconsRef.current.forEach(icon => icon.isHidden = false);

      for (let i = 0; i < iconsRef.current.length; i++) {
        for (let j = i + 1; j < iconsRef.current.length; j++) {
          const icon1 = iconsRef.current[i];
          const icon2 = iconsRef.current[j];
          const dx = icon1.x - icon2.x;
          const dy = icon1.y - icon2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < collisionThreshold) {
            icon2.isHidden = true;
            icon2.hideUntil = now + 1000; 
          }
        }
      }
      
      iconsRef.current.forEach(icon => {
        // While still off-screen (pre-entry), skip the wall-bounce check entirely —
        // otherwise it immediately reverses the inward velocity that's supposed
        // to bring the icon onto the screen in the first place.
        if (icon.hasEnteredX) {
          if (icon.x < 0 || icon.x > containerWidth - iconSize) icon.vx *= -1;
        } else if (icon.x >= 0 && icon.x <= containerWidth - iconSize) {
          icon.hasEnteredX = true;
        }
        if (icon.y < 0 || icon.y > containerHeight - iconSize) icon.vy *= -1;

        icon.x += icon.vx;
        icon.y += icon.vy;

        if (icon.rotates) {
          icon.rotation += icon.rotationSpeed;
          icon.el.style.transform = `translate(${icon.x}px, ${icon.y}px) rotate(${icon.rotation}deg)`;
        } else {
          icon.el.style.transform = `translate(${icon.x}px, ${icon.y}px)`;
        }

        if (icon.isHidden || now < icon.hideUntil) {
            icon.el.style.opacity = '0';
        } else {
            icon.el.style.opacity = '0.08';
        }
      });

      animationFrameId.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [currentSection]);

  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Deep-link a section (used by the Verify page's header nav → /?tab=components)
    const tab = params.get('tab');
    if (tab && ['refurbished', 'components', 'desktopTower', 'about', 'home', 'games'].includes(tab)) {
      window.history.replaceState({}, document.title, window.location.pathname);
      setCurrentSection(tab);
      return;
    }

    const productType = params.get('productType');
    const productId   = params.get('productId');

    if (!productType || !productId) return;

    // Clean the URL immediately so browser-back and home-nav work correctly
    window.history.replaceState({}, document.title, window.location.pathname);

    let node, storageFolder, origin;
    switch (productType) {
      case 'refurbished':  node = 'Refurbished';   storageFolder = 'Refurbished'; origin = 'laptop';     break;
      case 'components':   node = 'components';     storageFolder = 'components';  origin = 'components'; break;
      case 'desktopTower': node = 'desktop_build';  storageFolder = 'Desktop';     origin = 'desktop';    break;
      default: return;
    }

    (async () => {
      try {
        const itemData = await fetchData(`${node}/${productId}`);
        if (!itemData) { setCurrentSection('home'); return; }
        const imageKey = itemData.Cover_image || itemData.CoverImage || itemData.Image || '';
        const imageUrl = imageKey ? await fetchImageFromStorage(storageFolder, imageKey).catch(() => '') : '';
        const fullItem = { ...itemData, key: productId, imageUrl };
        if (origin === 'laptop') {
          setSelectedLaptop(fullItem);
          await fetchCarouselImages(fullItem, 'Refurbished');
          setCurrentSection('laptopDetail');
        } else {
          setSelectedComponent({ ...fullItem, __origin: origin === 'desktop' ? 'desktop' : 'components' });
          await fetchCarouselImages(fullItem, storageFolder);
          setCurrentSection('componentDetail');
        }
      } catch (err) {
        console.error('Shared product load failed:', err);
        setCurrentSection('home');
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (['componentDetail', 'laptopDetail', 'gameDetail'].includes(currentSection)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentSection]);

  useEffect(() => {
    const TAB_LABELS = {
      home: 'Home',
      refurbished: 'Refurbished Laptops',
      components: 'PC Components',
      desktopTower: 'Desktop Builds',
      games: 'Games',
      about: 'About / Contact',
    };
    if (TAB_LABELS[currentSection]) {
      trackVisit('tab', TAB_LABELS[currentSection]);
    } else if (currentSection === 'laptopDetail' && selectedLaptop) {
      trackVisit('product', selectedLaptop.Name || 'Unnamed Laptop');
    } else if (currentSection === 'componentDetail' && selectedComponent) {
      trackVisit('product', selectedComponent.Name || 'Unnamed Component');
    } else if (currentSection === 'gameDetail' && selectedGame) {
      trackVisit('product', selectedGame.Name || 'Unnamed Game');
    }
  }, [currentSection, selectedLaptop, selectedComponent, selectedGame]);

  useEffect(() => {
    // Fetch each section's data (and its Storage images) only once per visit —
    // re-entering a tab previously refetched everything from Firebase every time.
    const fetched = fetchedRef.current;
    if (currentSection === 'games' && !fetched.games) { fetched.games = true; loadGames(); }
    else if (currentSection === 'refurbished' && !fetched.laptops) { fetched.laptops = true; loadLaptops(); }
    else if (currentSection === 'components' && !fetched.components) { fetched.components = true; loadComponents(); }
    else if (currentSection === 'desktopTower' && !fetched.desktops) { fetched.desktops = true; loadDesktops(); }
  }, [currentSection]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowReviews(true);
      const script = document.createElement('script');
      script.src = "https://widget.taggbox.com/embed-lite.min.js";
      script.async = true;
      document.body.appendChild(script);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const SkeletonGrid = () => (
    <div className="loading-grid">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="skeleton-card">
          <div className="skeleton-img" />
          <div className="skeleton-text" />
          <div className="skeleton-text short" />
        </div>
      ))}
    </div>
  );

  const loadData = async (node, storageFolder, setData) => {
    try {
      setLoading(true);
      const data = await fetchData(node);
      if (data) {
        const list = await Promise.all(
          Object.keys(data).map(async key => {
            const item = data[key];
            const imageKey = item.Cover_image || item.CoverImage || item.Image || '';
            const imageUrl = imageKey ? await fetchImageFromStorage(storageFolder, imageKey).catch(() => '') : '';
            return { ...item, key, imageUrl };
          })
        );
        setData(list);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error(`Error loading ${node}:`, error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const loadComponents = () => loadData('components', 'components', setComponents);
  const loadDesktops = () => loadData('desktop_build', 'Desktop', setDesktops);
  const loadGames = () => loadData('Games', 'Games', setGames);
  const loadLaptops = () => loadData('Refurbished', 'Refurbished', setLaptops);

  const checkImgOrientation = (url) => {
    if (!url) return;
    const img = new window.Image();
    img.onload = () => setImgIsTall(img.naturalHeight > img.naturalWidth * 0.9);
    img.src = url;
  };

  useEffect(() => {
    setImgIsTall(false);
    if (carouselImages[0]) checkImgOrientation(carouselImages[0]);
  }, [carouselImages]);

  const fetchCarouselImages = async (item, folder) => {
    const imageNames = (item.Images && String(item.Images).trim().length > 0)
      ? String(item.Images).split(',').map(s => s.trim()).filter(Boolean)
      : (item.Image ? [item.Image] : []);
    
    try {
      const urls = await Promise.all(
        imageNames.length > 0
          ? imageNames.map(name => fetchImageFromStorage(folder, name).catch(() => ''))
          : (item.imageUrl ? [item.imageUrl] : [])
      );
      setCarouselImages(urls.filter(Boolean));
    } catch (err) {
      console.error('Error fetching detail images:', err);
      setCarouselImages(item.imageUrl ? [item.imageUrl] : []);
    }
  };
  
  const handleComponentClick = async (component, origin = 'components') => {
    setSelectedComponent({ ...component, __origin: origin });
    const folder = origin === 'desktop' ? 'Desktop' : 'components';
    await fetchCarouselImages(component, folder);
    setCurrentSection('componentDetail');
  };

  const handleLaptopClick = async (laptop) => {
    setSelectedLaptop(laptop);
    await fetchCarouselImages(laptop, 'Refurbished');
    setCurrentSection('laptopDetail');
  };

  const handleGameClick = async (game) => {
    setSelectedGame(game);
    await fetchCarouselImages(game, 'Games');
    setCurrentSection('gameDetail');
  };

  const handleNavClick = (section) => {
    if (section === 'home') {
      window.location.href = window.location.origin;
    } else {
      setCurrentSection(section);
      setIsNavOpen(false);
    }
  };

  const dismissMenuHint = () => {
    setShowMenuHint(false);
    setHintVisible(false);
  };

  const toggleMobileNav = () => {
    setIsNavOpen(o => !o);
    dismissMenuHint();
  };

  const measureNavToggle = () => {
    const el = navToggleRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setHintRect({ top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height });
  };

  // Wait until the welcome text has settled AND the reviews carousel has
  // loaded in, then measure the REAL hamburger button's on-screen position
  // and trigger the spotlight. Anchoring to the actual measured rect (instead
  // of a guessed pixel offset) is what keeps the arrow pointing at the icon
  // on every screen size.
  useEffect(() => {
    if (!showMenuHint || !showReviews || currentSection !== 'home') return;
    const t = setTimeout(() => {
      measureNavToggle();
      setHintVisible(true);
    }, 900);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMenuHint, showReviews, currentSection]);

  // Keep the spotlight aligned with the button across orientation changes,
  // and auto-hide (without marking "seen") if it's never tapped.
  useEffect(() => {
    if (!hintVisible) return;
    window.addEventListener('resize', measureNavToggle);
    const t = setTimeout(() => setHintVisible(false), 5500);
    return () => { window.removeEventListener('resize', measureNavToggle); clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hintVisible]);

  const closeChatOverlay = () => setShowChatOverlay(false);

  const redirectToWhatsApp = () => {
    let productName = 'the product';
    let productType = '';
    let productId = '';
    if (selectedComponent) {
      productName = selectedComponent.Name;
      productType = selectedComponent.__origin === 'desktop' ? 'desktopTower' : 'components';
      productId = selectedComponent.key;
    } else if (selectedLaptop) {
      productName = selectedLaptop.Name;
      productType = 'refurbished';
      productId = selectedLaptop.key;
    } else if (selectedGame) {
      productName = selectedGame.Name;
    }

    const phoneNumber = '9164915365';
    // Full sentence first, then a blank line, then the shareable deep-link to
    // this exact product (same format as the Share button).
    let message = `I am interested in ${productName} which was listed on your website.`;
    if (productType && productId) {
      const productLink = `${window.location.origin}${window.location.pathname}?productType=${productType}&productId=${productId}`;
      message += `\n\nProduct Link: ${productLink}`;
    }
    const whatsappUrl = `https://wa.me/91${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };
  
  // ▼▼▼ UPDATED SHARE FUNCTION ▼▼▼
  const handleShare = async () => {
    let productType = '';
    let productId = '';
    let productName = '';
    let productImageUrl = '';

    if (selectedLaptop) {
        productType = 'refurbished';
        productId = selectedLaptop.key;
        productName = selectedLaptop.Name;
        productImageUrl = selectedLaptop.imageUrl;
    } else if (selectedComponent) {
        productType = selectedComponent.__origin === 'desktop' ? 'desktopTower' : 'components';
        productId = selectedComponent.key;
        productName = selectedComponent.Name;
        productImageUrl = selectedComponent.imageUrl;
    } else {
        return;
    }

    const shareUrl = `${window.location.origin}${window.location.pathname}?productType=${productType}&productId=${productId}`;
    
    const shareData = {
        title: `Check out: ${productName}`,
        text: `I found this amazing deal on ${productName} at Square Computers!`,
        url: shareUrl,
    };

    // Check if Web Share API is supported
    if (navigator.share) {
      // Try to fetch the image and convert it to a file
      try {
        const response = await fetch(productImageUrl);
        const blob = await response.blob();
        const file = new File([blob], 'product.png', { type: blob.type });

        // Check if the browser can share files
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            ...shareData,
            files: [file],
          });
        } else {
          // Fallback to sharing without the image if files are not supported
          await navigator.share(shareData);
        }
      } catch (err) {
        console.error('Error fetching image for sharing, sharing text only:', err);
        // If image fetch fails, share text and URL only
        await navigator.share(shareData);
      }
    } else {
        // Fallback for browsers that do not support Web Share API
        navigator.clipboard.writeText(shareUrl).then(() => {
            alert('Link copied to clipboard!');
        }).catch(err => {
            console.error('Failed to copy link:', err);
        });
    }
  };

  return (
    <div className="home-container">
      <header className="home-header">
        <div className="home-logo" onClick={() => handleNavClick('home')}>
          <h1>SQUARE</h1>
          <h2>COMPUTERS</h2>
        </div>
        <div className="nav-toggle" ref={navToggleRef} onClick={toggleMobileNav}>☰</div>
        <nav className="home-nav">
          <ul>
            <li onClick={() => handleNavClick('refurbished')}>REFURBISHED</li>
            <li onClick={() => handleNavClick('components')}>PC COMPONENTS</li>
            <li onClick={() => handleNavClick('desktopTower')}>DESKTOP TOWER</li>
            {/* Link to the Games section */}
            {/* <li onClick={() => handleNavClick('games')}>GAMES</li> */}
            <li onClick={() => handleNavClick('about')}>ABOUT</li>
            <li onClick={() => (window.location.href = window.location.origin + '/verify')}>VERIFY BILL/QUOTE</li>
          </ul>
        </nav>
      </header>

      {/* Mobile-only onboarding spotlight. Once the welcome text + reviews
          have loaded, the page smoothly blurs and a spring arrow is drawn
          from the centre of the page up to the REAL hamburger button (its
          on-screen rect is measured into hintRect, and the SVG path is
          computed from that + the live viewport size — no guessed offsets,
          so it always points exactly at the button on any screen).
          Overlay / ring / arrow / message are SIBLINGS with independent
          z-index: the blur sits just under the header (1000) so the button
          stays sharp, while the ring + arrow sit above it. */}
      {hintVisible && hintRect && !isNavOpen && currentSection === 'home' && (() => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const endX = hintRect.left + hintRect.width / 2;   // button centre X
        const endY = hintRect.bottom + 12;                 // just below button
        const startX = vw / 2;                             // centre of page
        const startY = Math.max(endY + 200, vh * 0.52);
        // Curve rises from centre, drifts slightly toward the button's column,
        // then approaches vertically from below so the head points straight up.
        const p1x = startX + (endX - startX) * 0.08;
        const p1y = startY - (startY - endY) * 0.5;
        const p2x = endX;
        const p2y = endY + 80;
        const pathD = `M ${startX} ${startY} C ${p1x} ${p1y}, ${p2x} ${p2y}, ${endX} ${endY}`;
        const headD = `M ${endX - 10} ${endY + 14} L ${endX} ${endY} L ${endX + 10} ${endY + 14}`;
        return (
          <>
            <div className="menu-hint-overlay" onClick={dismissMenuHint} />
            <div
              className="menu-hint-ring"
              style={{ top: hintRect.top - 7, left: hintRect.left - 7, width: hintRect.width + 14, height: hintRect.height + 14 }}
            />
            <div className="menu-hint-arrow-layer" style={{ transformOrigin: `${startX}px ${startY}px` }}>
              <svg width={vw} height={vh} viewBox={`0 0 ${vw} ${vh}`} fill="none" xmlns="http://www.w3.org/2000/svg">
                <path className="menu-hint-path" d={pathD} pathLength="1" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" />
                <path className="menu-hint-head" d={headD} stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="menu-hint-msg" style={{ top: startY + 20 }}>Tap the menu to explore</div>
          </>
        );
      })()}

      <div className={`mobile-nav ${isNavOpen ? 'active' : ''}`}>
        <span className="close-btn" onClick={() => setIsNavOpen(false)}>×</span>
        <ul>
          <li onClick={() => handleNavClick('refurbished')}>REFURBISHED</li>
          <li onClick={() => handleNavClick('components')}>PC COMPONENTS</li>
          <li onClick={() => handleNavClick('desktopTower')}>DESKTOP TOWER</li>
          {/* Link to the Games section */}
          {/* <li onClick={() => handleNavClick('games')}>GAMES</li> */}
          <li onClick={() => handleNavClick('about')}>ABOUT</li>
          <li onClick={() => (window.location.href = window.location.origin + '/verify')}>VERIFY BILL/QUOTE</li>
        </ul>
      </div>

      <main className="home-main">
        {currentSection === 'home' && (
          <>
            <div className="animated-icons-container" ref={animationContainerRef}>
              {animatedIcons.map((icon, index) => (
                <img key={index} src={icon} className="animated-icon" alt="" />
              ))}
            </div>
            <h1 className="welcome-text">Welcome to Square Computers</h1>
            <h2 className="subtext">Quality Refurbished Laptops &amp; Custom PC Builds</h2>
            <h2 className="subtext">Trusted since 2014 · Saligrama, Udupi</h2>
            {showReviews && <ReviewsCarousel />}
          </>
        )}

        {currentSection === 'refurbished' && (
          <section className="refurbished-section">
            {loading ? <SkeletonGrid /> : (
              <div className="refurbished-container">
                {laptops.length > 0 ? laptops.map((laptop, i) => {
                  const badges = getBrandBadges(laptop);
                  return (
                  <div key={laptop.key} className="laptop-card" style={{ '--i': i }} onClick={() => handleLaptopClick(laptop)}>
                    {badges.length > 0 && (
                      <div className="card-brand-badges">
                        {badges.map((b, bi) => <img key={bi} src={b.src} alt={b.alt} title={b.alt} />)}
                      </div>
                    )}
                    <img src={laptop.imageUrl} alt={laptop.Name} loading="lazy" decoding="async" />
                    <h3>{laptop.Name}</h3>
                    <p className="price-tag">₹{fmtPrice(laptop.Price)}</p>
                  </div>
                  );
                }) : <p style={{gridColumn:'1/-1',textAlign:'center',color:'#6b7280'}}>No refurbished laptops available.</p>}
              </div>
            )}
          </section>
        )}

        {currentSection === 'components' && (
          <section className="components-section">
            {loading ? <SkeletonGrid /> : (
              <div className="refurbished-container">
                {components.length > 0 ? components.map((component, i) => (
                  <div key={component.key} className="laptop-card" style={{ '--i': i }} onClick={() => handleComponentClick(component, 'components')}>
                    <img src={component.imageUrl} alt={component.Name} loading="lazy" decoding="async" />
                    <h3>{component.Name}</h3>
                    <p className="price-tag">₹{fmtPrice(component.Price)}</p>
                  </div>
                )) : <p style={{gridColumn:'1/-1',textAlign:'center',color:'#6b7280'}}>No PC components available.</p>}
              </div>
            )}
          </section>
        )}

        {currentSection === 'desktopTower' && (
          <section className="components-section">
            {loading ? <SkeletonGrid /> : (
              <div className="refurbished-container">
                {desktops.length > 0 ? desktops.map((desktop, i) => (
                  <div key={desktop.key} className="laptop-card" style={{ '--i': i }} onClick={() => handleComponentClick(desktop, 'desktop')}>
                    <img src={desktop.imageUrl} alt={desktop.Name} loading="lazy" decoding="async" />
                    <h3>{desktop.Name}</h3>
                    <p className="price-tag">₹{fmtPrice(desktop.Price)}</p>
                  </div>
                )) : <p style={{gridColumn:'1/-1',textAlign:'center',color:'#6b7280'}}>No Desktop builds available.</p>}
              </div>
            )}
          </section>
        )}

        {currentSection === 'games' && (
          <section className="games-section">
            {loading ? <SkeletonGrid /> : (
              <div className="refurbished-container">
                {games.length > 0 ? games.map((game, i) => (
                  <div key={game.key} className="laptop-card" style={{ '--i': i }} onClick={() => handleGameClick(game)}>
                    <img src={game.imageUrl || ''} alt={game.Name || 'Game'} loading="lazy" decoding="async" />
                    <h3>{game.Name || 'Untitled Game'}</h3>
                    {game.Price && <p className="price-tag">₹{fmtPrice(game.Price)}</p>}
                  </div>
                )) : <p style={{gridColumn:'1/-1',textAlign:'center',color:'#6b7280'}}>No games available.</p>}
              </div>
            )}
          </section>
        )}
        
        {currentSection === 'componentDetail' && selectedComponent && (
          selectedComponent.__origin === 'desktop' ? (
            <section className="laptop-detail-section">
              <button className="back-button" onClick={() => setCurrentSection('desktopTower')}><img src={backIcon} alt="Back" /></button>
              <button className="share-button" onClick={handleShare}><img src={shareIcon} alt="Share" /></button>
              <ImageGallery
                items={carouselImages.map(img => ({ original: img, thumbnail: img }))}
                showPlayButton={false}
                onSlide={(idx) => checkImgOrientation(carouselImages[idx])}
              />
              <div className="laptop-details" style={{
                transform: imgIsTall ? 'translateY(16px)' : 'translateY(0)',
                transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1)',
              }}>
                <h3>{selectedComponent.Name}</h3>
                <h2>Price: ₹{fmtPrice(selectedComponent.Price)}</h2>
                {selectedComponent.Processor && <p className="spec-row"><img src={processorIcon} alt="Processor" className="icon" /> {selectedComponent.Processor}</p>}
                {selectedComponent.Ram && <p className="spec-row"><img src={ramIcon} alt="RAM" className="icon" /> {selectedComponent.Ram}</p>}
                {selectedComponent.Storage && <p className="spec-row"><img src={storageIcon} alt="Storage" className="icon" /> {selectedComponent.Storage}</p>}
                {selectedComponent.Display && <p className="spec-row"><img src={displayIcon} alt="Display" className="icon" /> {selectedComponent.Display}</p>}
                <ul className="component-details-list">
                  {Object.keys(selectedComponent).map(key => {
                    if (['Category', 'Cover_image', 'Price', 'Name', 'key', 'imageUrl', 'Images', '__origin', 'Processor', 'Ram', 'Storage', 'Display'].includes(key)) return null;
                    const hasPngIcon = !!ICON_MAP[String(key).toLowerCase()];
                    return (
                      <li key={key} className="detail-row">
                        <SpecIcon fieldKey={key} />
                        <span className="detail-value">
                          {!hasPngIcon && <strong>{key}</strong>}
                          {!hasPngIcon && ': '}
                          {String(selectedComponent[key])}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <button className="book-now-button" onClick={redirectToWhatsApp}>
                  <img src={whatsappIcon} alt="" className="whatsapp-icon-anim" style={{ width:20, height:20, marginRight:8, verticalAlign:'middle' }} />
                  Book Now
                </button>
              </div>
            </section>
          ) : (
            <section className="component-detail-section">
              <button className="back-button" onClick={() => setCurrentSection('components')}>
                <img src={backIcon} alt="Back" />
              </button>
              <button className="share-button" onClick={handleShare}>
                <img src={shareIcon} alt="Share" />
              </button>
              <ImageGallery
                items={carouselImages.map(img => ({ original: img, thumbnail: img }))}
                showPlayButton={false}
                onSlide={(idx) => checkImgOrientation(carouselImages[idx])}
              />
              <div className="component-details" style={{
                transform: imgIsTall ? 'translateY(16px)' : 'translateY(0)',
                transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1)',
              }}>
                <h3>{selectedComponent.Name}</h3>
                <p className="component-price">Price: ₹{fmtPrice(selectedComponent.Price)}</p>
                <ul className="component-details-list">
                  {Object.keys(selectedComponent).map(key => {
                    if (['Category', 'Cover_image', 'Price', 'Name', 'key', 'imageUrl', 'Images', '__origin'].includes(key)) return null;
                    const hasPngIcon = !!ICON_MAP[String(key).toLowerCase()];
                    return (
                      <li key={key} className="detail-row">
                        <SpecIcon fieldKey={key} />
                        <span className="detail-value">
                          {!hasPngIcon && <strong>{key}</strong>}
                          {!hasPngIcon && ': '}
                          {String(selectedComponent[key])}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <button className="book-now-button" onClick={redirectToWhatsApp}>
                  <img src={whatsappIcon} alt="" className="whatsapp-icon-anim" style={{ width:20, height:20, marginRight:8, verticalAlign:'middle' }} />
                  Book Now
                </button>
              </div>
            </section>
          )
        )}
        
        {currentSection === 'laptopDetail' && selectedLaptop && (
            <section className="laptop-detail-section">
                <button className="back-button" onClick={() => setCurrentSection('refurbished')}><img src={backIcon} alt="Back" /></button>
                <button className="share-button" onClick={handleShare}><img src={shareIcon} alt="Share" /></button>
                <ImageGallery
                  items={carouselImages.map(img => ({ original: img, thumbnail: img }))}
                  showPlayButton={false}
                  onSlide={(idx) => checkImgOrientation(carouselImages[idx])}
                />
                <div className="laptop-details" style={{
                  transform: imgIsTall ? 'translateY(16px)' : 'translateY(0)',
                  transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1)',
                }}>
                    <div className="ld-name-row">
                      <h3>{selectedLaptop.Name}</h3>
                      {getBrandBadges(selectedLaptop).length > 0 && (
                        <div className="ld-name-badges">
                          {getBrandBadges(selectedLaptop).map((b, bi) => <img key={bi} src={b.src} alt={b.alt} title={b.alt} />)}
                        </div>
                      )}
                    </div>
                    <h2>Price: ₹{fmtPrice(selectedLaptop.Price)}</h2>
                    {selectedLaptop.Processor  && <p className="spec-row"><img src={processorIcon}     alt="Processor"    className="icon" /> {selectedLaptop.Processor}</p>}
                    {selectedLaptop.ClockSpeed && <p className="spec-row"><img src={cpuClockSpeedIcon} alt="Clock Speed"  className="icon" /> {selectedLaptop.ClockSpeed}</p>}
                    {selectedLaptop.Ram        && <p className="spec-row"><img src={ramIcon}           alt="RAM"          className="icon" /> {selectedLaptop.Ram}</p>}
                    {selectedLaptop.Storage    && <p className="spec-row"><img src={storageIcon}       alt="Storage"      className="icon" /> {selectedLaptop.Storage}</p>}
                    {selectedLaptop.Display    && <p className="spec-row"><img src={displayIcon}       alt="Display"      className="icon" /> {selectedLaptop.Display}</p>}
                    {selectedLaptop.RefreshRate&& <p className="spec-row"><img src={refreshRateIcon}   alt="Refresh Rate" className="icon" /> {selectedLaptop.RefreshRate}</p>}
                    {selectedLaptop.GPU        && <p className="spec-row"><img src={gpuIcon}           alt="GPU"          className="icon" /> {selectedLaptop.GPU}</p>}
                    {selectedLaptop.Battery    && <p className="spec-row"><img src={batteryIcon}       alt="Battery"      className="icon" /> {selectedLaptop.Battery}</p>}
                    {selectedLaptop.OS         && <p className="spec-row"><img src={osIcon}            alt="OS"           className="icon" /> {selectedLaptop.OS}</p>}
                    <button className="book-now-button" onClick={redirectToWhatsApp}>
                      <img src={whatsappIcon} alt="" className="whatsapp-icon-anim" style={{ width:20, height:20, marginRight:8, verticalAlign:'middle' }} />
                      Book Now
                    </button>
                </div>
            </section>
        )}
        
        {currentSection === 'gameDetail' && selectedGame && (
            <section className="game-detail-section">
                <button className="back-button" onClick={() => setCurrentSection('games')}><img src={backIcon} alt="Back" /></button>
                <ImageGallery
                  items={carouselImages.map(img => ({ original: img, thumbnail: img }))}
                  showPlayButton={false}
                  onSlide={(idx) => checkImgOrientation(carouselImages[idx])}
                />
                <div className="game-details" style={{
                  transform: imgIsTall ? 'translateY(16px)' : 'translateY(0)',
                  transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1)',
                }}>
                    <h3>{selectedGame.Name}</h3>
                    {selectedGame.Price && <h2>Price: ₹{fmtPrice(selectedGame.Price)}</h2>}
                    <ul className="component-details-list">
                        {Object.keys(selectedGame).map(key => {
                            if (['Name', 'Price', 'Image', 'key', 'imageUrl', 'Images'].includes(key)) return null;
                            return (
                                <li key={key} className="detail-row">
                                    <SpecIcon fieldKey={key} />
                                    <strong>{key}:</strong>
                                    <span>{String(selectedGame[key])}</span>
                                </li>
                            );
                        })}
                    </ul>
                    <button className="book-now-button" onClick={redirectToWhatsApp}>
                      <img src={whatsappIcon} alt="" className="whatsapp-icon-anim" style={{ width:20, height:20, marginRight:8, verticalAlign:'middle' }} />
                      Book Now
                    </button>
                </div>
            </section>
        )}
        {currentSection === 'about' && (
          <section className="about-section">
            <div className="animated-icons-container" ref={animationContainerRef}>
              {animatedIcons.map((icon, index) => (
                <img key={index} src={icon} className="animated-icon" alt="" />
              ))}
            </div>
            <div className="about-content">
              <p className="about-text-main">Computer Sales and Services</p>
              <p className="about-text-sub">Since 2014</p>
              <img src={ownerpic} alt="Owner" className="owner-image" />
              <p className="owner-name">Sharath Kumar</p>
              <div className="contact-icons-row">
                <a href="tel:+919164915365"><img src={phoneIcon} alt="Phone" className="contact-icon" /></a>
                <a href="https://www.instagram.com/square_saligrama365" target="_blank" rel="noopener noreferrer"><img src={instagramIcon} alt="Instagram" className="social-icon" /></a>
                <a href="https://wa.me/919164915365" target="_blank" rel="noopener noreferrer"><img src={whatsappIcon} alt="WhatsApp" className="contact-icon" /></a>
              </div>
              <div className="social-media-icons">
                  <a href="mailto:squaresaligrama@gmail.com"><img src={gmailIcon} alt="Gmail" className="contact-icon" /></a>
                  <a href="https://www.facebook.com/share/XiPRkJ8HygFNjRuj/" target="_blank" rel="noopener noreferrer"><img src={facebookIcon} alt="Facebook" className="social-icon" /></a>
              </div>
              <div className="maps-location">
                <iframe title="Square Computers location" src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3879.620641682142!2d74.70996099999999!3d13.497458000000002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTPCsDI5JzUwLjkiTiA3NMKwNDInMzUuOSJF!5e0!3m2!1sen!2sin!4v1760375124682!5m2!1sen!2sin" width="600" height="450" style={{border:"0"}} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
              </div>
              <p className="copyright-text">© Square Computers, All rights reserved</p>
            </div>
          </section>
        )}
      </main>

      {showChatOverlay && (
        <div className="chat-now-overlay" onClick={closeChatOverlay}>
          <div className="chat-now-card" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={closeChatOverlay}>
              <img src={closeIcon} alt="Close" />
            </button>
            <h3>Contact Us</h3>
            <p>Ready to book or have questions? Chat with us directly on WhatsApp for a quick response.</p>
            <button className="book-now-button" onClick={redirectToWhatsApp}>
              Chat on WhatsApp
            </button>
          </div>
        </div>
      )}

      {!['laptopDetail', 'componentDetail', 'gameDetail', 'about'].includes(currentSection) && (
        <a
          href="https://wa.me/919164915365"
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-fab"
          aria-label="Chat on WhatsApp"
        >
          <img src={whatsappIcon} alt="WhatsApp" />
        </a>
      )}
    </div>
  );
}

export default Home;